import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CheckCircle2, ArrowLeft } from "lucide-react";
import { useI18n } from "../lib/i18n";

export default function ResetPasswordPage() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="mx-auto flex max-w-md flex-col px-6 py-16 text-center" data-testid="reset-no-token">
        <h1 className="font-heading text-2xl font-semibold text-[#1A2024]">{t("reset.title")}</h1>
        <p className="mx-auto mt-3 max-w-sm font-body text-sm text-[#5C6873]">{t("reset.no_token")}</p>
        <Link to="/forgot-password" className="mt-6 font-body text-sm text-[#2E3A46] hover:underline" data-testid="reset-request-link">
          {t("forgot.title")}
        </Link>
      </div>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError(t("reset.mismatch"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || t("reset.invalid"));
    }
    setLoading(false);
  };

  if (done) {
    return (
      <div className="mx-auto flex max-w-md flex-col px-6 py-16 text-center" data-testid="reset-success">
        <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#4A7C59]/12 text-[#4A7C59]">
          <CheckCircle2 size={26} />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-semibold text-[#1A2024]">{t("reset.success_title")}</h1>
        <p className="mx-auto mt-3 max-w-sm font-body text-sm text-[#5C6873]">{t("reset.success_sub")}</p>
        <Link to="/login" className="mt-6" data-testid="reset-go-login">
          <Button className="bg-[#2E3A46] text-white hover:bg-[#1A2024]">{t("reset.go_login")}</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-md flex-col px-6 py-16" data-testid="reset-page">
      <h1 className="font-heading text-3xl font-semibold text-[#1A2024]">{t("reset.title")}</h1>
      <p className="mt-2 font-body text-sm text-[#5C6873]">{t("reset.sub")}</p>

      <form onSubmit={onSubmit} className="mt-8 space-y-5">
        <div>
          <Label htmlFor="password" className="font-body text-sm text-[#1A2024]">{t("reset.password")}</Label>
          <Input
            id="password"
            data-testid="reset-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            className="mt-1.5 h-11 border-[#E2E8F0] bg-white"
          />
          <p className="mt-1 font-body text-xs text-[#5C6873]">{t("auth.password_hint")}</p>
        </div>
        <div>
          <Label htmlFor="confirm" className="font-body text-sm text-[#1A2024]">{t("reset.confirm")}</Label>
          <Input
            id="confirm"
            data-testid="reset-confirm-input"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            className="mt-1.5 h-11 border-[#E2E8F0] bg-white"
          />
        </div>

        {error && (
          <div className="rounded-md border border-[#9E4747]/30 bg-[#9E4747]/5 px-3 py-2 font-body text-sm text-[#9E4747]" data-testid="reset-error">
            {error}
          </div>
        )}

        <Button type="submit" disabled={loading} data-testid="reset-submit-btn" className="h-11 w-full bg-[#2E3A46] text-white hover:bg-[#1A2024]">
          {loading ? t("reset.updating") : t("reset.submit")}
        </Button>
      </form>

      <Link to="/login" className="mt-6 inline-flex items-center gap-1 font-body text-sm text-[#2E3A46] hover:underline" data-testid="reset-back-link">
        <ArrowLeft size={14} /> {t("forgot.back_to_login")}
      </Link>
    </div>
  );
}
